import { useEffect, useRef, useState } from "react";
import type { TrackPublication } from "livekit-client";
import { useServices } from "../app/services.ts";
import { duration, label, title, VOXIE_ID } from "../data/display.ts";
import { useStore } from "../data/store.ts";
import { Avatar } from "../ui/Avatar.tsx";
import { Icon } from "../ui/Icon.tsx";
import { type CallState, REACTIONS, useCall } from "./controller.ts";
import type { Peer } from "./room.ts";
import styles from "./CallView.module.css";

const LANGUAGES: [string, string][] = [
  ["de", "Deutsch"], ["en", "Englisch"], ["fr", "Französisch"], ["es", "Spanisch"], ["it", "Italienisch"], ["pt", "Portugiesisch"], ["nl", "Niederländisch"],
  ["pl", "Polnisch"], ["cs", "Tschechisch"], ["hu", "Ungarisch"], ["hr", "Kroatisch"], ["sr", "Serbisch"], ["bs", "Bosnisch"], ["sl", "Slowenisch"],
  ["ro", "Rumänisch"], ["bg", "Bulgarisch"], ["el", "Griechisch"], ["tr", "Türkisch"], ["uk", "Ukrainisch"], ["ru", "Russisch"], ["ar", "Arabisch"],
  ["fa", "Persisch"], ["zh", "Chinesisch"], ["ja", "Japanisch"], ["ko", "Koreanisch"], ["hi", "Hindi"], ["vi", "Vietnamesisch"],
];

const ENDED: Record<string, string> = {
  declined: "Abgelehnt",
  busy: "Besetzt",
  no_answer: "Keine Antwort",
  answered_elsewhere: "Auf einem anderen Gerät angenommen",
  dropped: "Verbindung abgebrochen",
  failed: "Anruf nicht möglich",
  voxie_unavailable: "Voxie ist gerade nicht erreichbar",
};

/** The running call in the content area (docs/web-designs screen 5). */
export function CallView({ onMinimize, onOpenChat }: { onMinimize: () => void; onOpenChat: (conversationId: string) => void }) {
  const { calls } = useServices();
  const call = useCall(calls);
  const names = useNames(call?.conversationId);
  const [more, setMore] = useState(false);
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  if (!call) return null;
  const ended = call.phase === "ended";
  const name = call.voxie ? "Voxie" : names.title;

  return (
    <main className={styles.call} aria-label="Anruf">
      <header className={styles.head}>
        <b>{name}</b>
        <span>{status(call)}</span>
        {call.screenOn && (
          <span className={styles.pill}>
            <Icon name="screen" size={16} /> Du teilst deinen Bildschirm
          </span>
        )}
        <span className={styles.grow} />
        <button className={styles.ghost} onClick={() => (onOpenChat(call.conversationId), onMinimize())}>
          <Icon name="chat" size={18} /> Zum Chat
        </button>
      </header>

      {call.voxie ? <VoxieStage call={call} /> : <Stage call={call} names={names.of} />}

      {call.captionsOn && <Captions call={call} names={names.of} />}
      {call.transcribing && <p className={styles.notice}>Untertitel an: deine Stimme wird live in Text umgewandelt, nicht gespeichert.</p>}
      {call.error && <p className={styles.error}>{call.error}</p>}

      <div className={styles.reactions} aria-live="polite">
        {call.reactions.map((r) => (
          <span key={`${r.from}${r.at}`}>{r.emoji}</span>
        ))}
      </div>

      {call.videoRequestFrom && (
        <div className={styles.dialog} role="dialog">
          <b>Zu Videoanruf wechseln?</b>
          <span>{names.of(call.videoRequestFrom)} hat die Kamera eingeschaltet.</span>
          <div>
            <button className={styles.primary} onClick={() => calls.answerVideoRequest(true)}>
              Kamera einschalten
            </button>
            <button className={styles.ghost} onClick={() => calls.answerVideoRequest(false)}>
              Nicht jetzt
            </button>
          </div>
        </div>
      )}
      {call.waiting && (
        <div className={styles.dialog} role="dialog">
          <b>{names.of(call.waiting.from)} {call.waiting.kind === "video" ? "ruft per Video an" : "ruft an"}</b>
          <div>
            <button className={styles.primary} onClick={() => void calls.switchToWaiting()}>
              Auflegen und annehmen
            </button>
            <button className={styles.ghost} onClick={() => calls.dismissWaiting()}>
              Ablehnen
            </button>
          </div>
        </div>
      )}

      {ended ? (
        <div className={styles.controls}>
          {call.outgoing && ["no_answer", "declined", "busy"].includes(call.endReason ?? "") && (
            <>
              <button className={styles.primary} onClick={() => (calls.close(), void calls.start(call.conversationId, call.kind))}>
                Erneut anrufen
              </button>
              <button className={styles.ghost} onClick={() => (calls.close(), onOpenChat(call.conversationId))}>
                Sprachnachricht
              </button>
            </>
          )}
          <button className={styles.ghost} onClick={() => calls.close()}>
            Schließen
          </button>
        </div>
      ) : (
        <div className={styles.controls}>
          <Control icon={call.micOn ? "mic" : "micOff"} label="Mikro" on={call.micOn} onClick={() => void calls.toggleMic()} />
          {!call.voxie && <Control icon="video" label="Kamera" on={call.cameraOn} onClick={() => void calls.toggleCamera()} />}
          {!call.voxie && "getDisplayMedia" in (navigator.mediaDevices ?? {}) && <Control icon="screen" label="Teilen" on={call.screenOn} onClick={() => void calls.toggleScreen()} />}
          {!call.voxie && <Control icon="cc" label="Untertitel" on={call.captionsOn} onClick={() => calls.toggleCaptions()} />}
          {call.voxie && <Control icon="close" label="Stopp" on={false} onClick={() => calls.stopVoxie()} />}
          {!call.voxie && <Control icon="more" label="Mehr" on={more} onClick={() => setMore(!more)} />}
          <Control icon="callEnd" label="Auflegen" on={false} red onClick={() => void calls.hangup()} />
        </div>
      )}

      {more && !ended && (
        <div className={styles.more} role="menu">
          <div className={styles.emojis}>
            {REACTIONS.map((e) => (
              <button key={e} onClick={() => (calls.react(e), setMore(false))} aria-label={`Reaktion ${e}`}>
                {e}
              </button>
            ))}
          </div>
          <button className={styles.menuItem} onClick={() => (calls.toggleHand(), setMore(false))}>
            ✋ {call.hands.includes(names.me) ? "Hand senken" : "Hand heben"}
          </button>
          <label className={styles.menuItem}>
            Übersetzen
            <select value={call.captionLanguage ?? ""} disabled={!call.captionsOn} onChange={(e) => calls.setCaptionLanguage(e.target.value || null)}>
              <option value="">Originalsprache</option>
              {LANGUAGES.map(([code, name]) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </select>
            {!call.captionsOn && <small>Mit Untertiteln</small>}
          </label>
        </div>
      )}
    </main>
  );
}

function status(call: CallState): string {
  switch (call.phase) {
    case "outgoing":
      return call.delivered ? "Klingelt …" : "Ruft an …";
    case "incoming":
      return call.kind === "video" ? "Videoanruf" : "Sprachanruf";
    case "connecting":
      return call.voxie ? "Voxie kommt ans Telefon …" : "Verbindet …";
    case "reconnecting":
      return "Verbindung wird wiederhergestellt …";
    case "active":
      return `${call.peers.length + 1 > 2 ? "Gruppenanruf" : call.kind === "video" ? "Videoanruf" : "Sprachanruf"} · ${duration(Date.now() - (call.connectedAt ?? Date.now()))}`;
    case "ended":
      if (call.error) return call.error;
      if (call.connectedAt && !call.endReason?.startsWith("answered")) return `Anruf beendet · ${duration(Date.now() - call.connectedAt)}`;
      return ENDED[call.endReason ?? ""] && (call.outgoing || !["declined", "no_answer"].includes(call.endReason ?? "")) ? ENDED[call.endReason!]! : "Anruf beendet";
  }
}

function useNames(conversationId: string | undefined) {
  const { conversation, me } = useStore((s) => ({ conversation: conversationId ? s.conversations.get(conversationId) : undefined, me: s.me }));
  const of = (id: string) => (id === me ? "Du" : id === VOXIE_ID ? "Voxie" : label(conversation?.members.find((m) => m.user.id === id)?.user).replace(/^Unbekannt$/, "Voxie-Anruf"));
  return { of, me: me ?? "", title: conversation ? title(conversation, me) : "Voxie-Anruf" };
}

function Stage({ call, names }: { call: CallState; names: (id: string) => string }) {
  const remote = call.peers.filter((p) => !p.isAgent);
  if (call.phase !== "active" && call.phase !== "reconnecting") {
    return (
      <div className={styles.waitingStage}>
        <Avatar id={call.from || call.conversationId} name={names(call.from)} size={120} />
      </div>
    );
  }
  const solo = remote.length <= 1;
  return (
    <div className={solo ? styles.one : remote.length + 1 <= 2 ? styles.grid1 : styles.grid2}>
      {remote.map((p) => (
        <Tile key={p.identity} peer={p} name={names(p.identity)} hand={call.hands.includes(p.identity)} big={solo} />
      ))}
      {call.me && <Tile peer={call.me} name="Du" hand={call.hands.includes(call.me.identity)} small={solo} mirrored={!call.me.screen} />}
    </div>
  );
}

function Tile({ peer, name, hand, big, small, mirrored }: { peer: Peer; name: string; hand: boolean; big?: boolean; small?: boolean; mirrored?: boolean }) {
  return (
    <div className={[styles.tile, peer.speaking ? styles.speaking : "", big ? styles.big : "", small ? styles.small : ""].join(" ")}>
      {peer.video ? <VideoOf publication={peer.video} contain={peer.screen} mirrored={mirrored} /> : <Avatar id={peer.identity} name={name} size={small ? 56 : 120} />}
      <span className={styles.tag}>
        {peer.micMuted && <Icon name="micOff" size={14} />}
        {hand && "✋ "}
        {name}
        {peer.weak && " · schwache Verbindung"}
      </span>
    </div>
  );
}

function VideoOf({ publication, contain, mirrored }: { publication: TrackPublication; contain: boolean; mirrored?: boolean }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = ref.current;
    const track = publication.track;
    if (!el || !track) return;
    track.attach(el);
    return () => void track.detach(el);
  }, [publication, publication.track]);
  return <video ref={ref} autoPlay playsInline muted className={contain ? styles.contain : mirrored ? styles.mirrored : undefined} />;
}

function Captions({ call, names }: { call: CallState; names: (id: string) => string }) {
  const newest = new Map<string, (typeof call.captions)[number]>();
  for (const c of call.captions) if (c.speaker !== call.me?.identity) newest.set(c.speaker, c);
  const lines = [...newest.values()].slice(-3);
  return (
    <div className={styles.captions} aria-live="polite">
      {call.translateFailed && <small>Übersetzung gerade nicht möglich – Untertitel im Original</small>}
      {lines.length === 0 && <small>Untertitel an – sobald jemand spricht, steht es hier.</small>}
      {lines.map((c) => (
        <p key={c.speaker} className={call.captionLanguage && c.translated === undefined ? styles.untranslated : undefined}>
          <b>{names(c.speaker)}:</b> {c.translated ?? c.text}
        </p>
      ))}
    </div>
  );
}

function VoxieStage({ call }: { call: CallState }) {
  const agent = call.peers.find((p) => p.isAgent);
  const speaking = call.me?.speaking;
  const text = !call.micOn
    ? "Stumm: Voxie hört dich nicht."
    : call.phase !== "active"
      ? "Voxie kommt ans Telefon …"
      : speaking
        ? "Ich höre dich …"
        : agent?.agentState === "thinking"
          ? "Moment …"
          : agent?.agentState === "speaking"
            ? "Fall mir ruhig ins Wort."
            : "Ich höre zu. Sprich einfach.";
  return (
    <div className={styles.voxie}>
      <div className={[styles.orb, agent?.agentState === "speaking" ? styles.orbSpeaking : "", agent?.agentState === "thinking" ? styles.orbThinking : ""].join(" ")} />
      <p className={styles.voxieState}>{text}</p>
      <div className={styles.voxieLines}>
        {call.agentLines.slice(-3).map((l) => (
          <p key={l.id} className={l.mine ? styles.mine : undefined}>
            {l.text}
          </p>
        ))}
      </div>
    </div>
  );
}

function Control({ icon, label: text, on, red, onClick }: { icon: Parameters<typeof Icon>[0]["name"]; label: string; on: boolean; red?: boolean; onClick: () => void }) {
  return (
    <button className={styles.control} onClick={onClick} aria-pressed={on} aria-label={text}>
      <span className={[styles.round, on ? styles.on : "", red ? styles.red : ""].join(" ")}>
        <Icon name={icon} size={24} />
      </span>
      {text}
    </button>
  );
}

/** A ringing call, over whatever is open. */
export function IncomingCall() {
  const { calls } = useServices();
  const call = useCall(calls);
  const names = useNames(call?.conversationId);
  if (!call || call.phase !== "incoming") return null;
  const kind = call.kind === "video" ? "Videoanruf" : "Sprachanruf";
  const reason = { urgent: "Dringend", quick: "Kurze Frage", casual: "Einfach so" }[call.ringReason ?? ""];
  const caller = call.voxie ? "Voxie" : names.of(call.from);
  return (
    <div className={styles.incomingBackdrop}>
      <div className={styles.incoming} role="alertdialog" aria-label={`${caller} ruft an`}>
        <Avatar id={call.from} name={caller} size={96} voxie={call.voxie} />
        <b>{caller}</b>
        <span>{reason ? `${kind} · ${reason}` : call.voxie ? (call.kind === "video" ? "Voxie-Videoanruf" : "Voxie-Sprachanruf") : kind}</span>
        <div className={styles.incomingButtons}>
          <button className={styles.control} onClick={() => void calls.decline()} aria-label="Ablehnen">
            <span className={`${styles.round} ${styles.red}`}>
              <Icon name="callEnd" size={26} />
            </span>
            Ablehnen
          </button>
          {call.kind === "video" && (
            <button className={styles.control} onClick={() => void calls.accept(true)} aria-label="Mit Video annehmen">
              <span className={`${styles.round} ${styles.green}`}>
                <Icon name="video" size={26} />
              </span>
              Mit Video
            </button>
          )}
          <button className={styles.control} onClick={() => void calls.accept(false)} aria-label="Annehmen">
            <span className={`${styles.round} ${styles.green}`}>
              <Icon name="call" size={26} />
            </span>
            Annehmen
          </button>
        </div>
      </div>
    </div>
  );
}

/** The bar on top while the call runs and a chat is open. */
export function CallBar({ onOpen }: { onOpen: () => void }) {
  const { calls } = useServices();
  const call = useCall(calls);
  const names = useNames(call?.conversationId);
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  if (!call || call.phase === "incoming") return null;
  return (
    <button className={styles.bar} onClick={onOpen}>
      <Icon name="call" size={18} />
      <b>{call.voxie ? "Voxie" : names.title}</b>
      <span>{status(call)}</span>
      <span className={styles.grow} />
      Zurück zum Anruf
    </button>
  );
}
